import type { HexColor } from "../../types.ts";
import type {
  ModelExtraGenerationParams,
  ModelGenerationParams,
} from "../boardState/stores/selectedModels/types.ts";
import type { ModelWithIsCompatibleAndTags } from "../types.ts";
import {
  type ANIMATE_GENERATION_TYPE,
  type BACKGROUND_REPLACE_QUALITY_PRESETS,
  type BACKGROUND_REPLACEMENT_MODES,
  type BOARD_WORKSPACES,
  type BRIEF_TO_IMAGE_GENERATION_TYPE,
  type COPY_AD_GENERATION_TYPE,
  type CREATE_TOOLS,
  type DESIGNER_TOOLS,
  type EDIT_ASSET_PANEL_TYPE,
  type EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE,
  type EDIT_TOOLS,
  type ENHANCE_GENERATION_TYPE,
  type EXPAND_GENERATION_TYPE,
  type GENERATION_RATIOS,
  type GENERATION_TYPES,
  type GENERATIVE_FILL_GENERATION_TYPE,
  type IMAGE_EDITION_TOOLS,
  type IMAGE_RESOURCE_TYPE,
  type IMAGE_TO_IMAGE_GENERATION_TYPE,
  type LORA_GENERATION_TYPE,
  MODEL_COMPATIBLE_TOOLS,
  type MODEL_GENERATION_TYPES,
  type NODE_STATUS,
  type OBJECT_RERENDERED_QUALITY_PRESETS,
  type RESOURCE_TYPES,
  type SCALE_FACTORS,
  type STYLE_TRANSFER_GENERATION_TYPE,
  type TEXT_TO_IMAGE_GENERATION_TYPE,
  type TOOLS,
  type UPSCALER_GENERATION_TYPE,
  type VIDEO_RESOURCE_TYPE,
  type ZERO_SHOT_CONSISTENCY_PRESETS,
  type ZERO_SHOT_GENERATION_TYPE,
} from "./constants.ts";
import type { StyleTransferInitImageInfluence } from "./Workspaces/CreateWorkspace/Tools/StyleTransferView/types.ts";
import type { VariationCreativityStrengthPreset } from "./Workspaces/EditWorkspace/Tools/EditAsset/EditImageWithPrompt/types.ts";
import type { UpscaleImaginationStrengthPreset } from "./Workspaces/EditWorkspace/Tools/EditAsset/Upscale/types.ts";
export type ImageStatus = "PROCESSING" | "SUCCEEDED" | "FAILED";

type PromptWithImageUuid = {
  uuid: string;
  prompt: string;
};

type GenerationRatioWithImageUuid = {
  uuid: string;
  generation_ratio: GenerationRatio;
};

export type Tag = {
  uuid: string;
  name: string;
};

export type Board = {
  uuid: string;
  name: string;
  tags: Tag[];
};

export type ResourceType = (typeof RESOURCE_TYPES)[number];

export type ModelGenerationType = (typeof MODEL_GENERATION_TYPES)[number];

export type ZeroShotConsistencyPreset =
  (typeof ZERO_SHOT_CONSISTENCY_PRESETS)[number];

export type BackgroundReplaceQualityPreset =
  (typeof BACKGROUND_REPLACE_QUALITY_PRESETS)[number];

type ObjectRerenderedQualityPreset =
  (typeof OBJECT_RERENDERED_QUALITY_PRESETS)[number];

export type BackgroundReplacementMode =
  (typeof BACKGROUND_REPLACEMENT_MODES)[number];

export type BaseModelGenerationParams = {
  uuid: string;
  model_generation_type: ModelGenerationType;
};

export type LoraModelGenerationParams = BaseModelGenerationParams & {
  model_generation_type: typeof LORA_GENERATION_TYPE;
  scale: number | null;
};

export type ZeroShotModelGenerationParams = BaseModelGenerationParams & {
  model_generation_type: typeof ZERO_SHOT_GENERATION_TYPE;
  zero_shot_consistency_preset: ZeroShotConsistencyPreset;
};

export type BackgroundReplaceModelGenerationParams =
  BaseModelGenerationParams & {
    model_generation_type: typeof ZERO_SHOT_GENERATION_TYPE;
    background_replace_quality_preset: BackgroundReplaceQualityPreset;
    resize_factor: number | null;
    generation_ratio: GenerationRatio | null;
    background_replacement_mode: BackgroundReplacementMode;
    is_upscaled: boolean;
    object_rerendered_quality_preset: ObjectRerenderedQualityPreset;
  };

type BaseGenerationParams = {
  generation_params: GenerationType;
};

export type TextToImageGenerationParams = BaseGenerationParams & {
  user_prompt: string | null;
  generation_ratio: GenerationRatio | null;
  references: ModelGenerationParams[] | null;
  generation_type: typeof TEXT_TO_IMAGE_GENERATION_TYPE;
};

export type EditImageWithPromptGenerationParams = BaseGenerationParams & {
  generation_type: typeof EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE;
  user_prompt: string | null;
  init_image_uuid: string | null;
};

export type ImageToImageGenerationParams = BaseGenerationParams & {
  generation_type: typeof IMAGE_TO_IMAGE_GENERATION_TYPE;
  user_prompt: string | null;
  init_image_uuid: string | null;
  prompt_strength: number | null;
  creativity_strength_preset: VariationCreativityStrengthPreset | null;
  references: ModelGenerationParams[] | null;
};

export type StyleTransferGenerationParams = BaseGenerationParams & {
  generation_type: typeof STYLE_TRANSFER_GENERATION_TYPE;
  user_prompt: string | null;
  creativity: number | null;
  colors_strength: number | null;
  structure_strength: number | null;
  init_image_uuid: string | null;
  init_image_color_influence: StyleTransferInitImageInfluence | null;
  references: ModelGenerationParams[] | null;
};

export type UpscalerGenerationParams = BaseGenerationParams & {
  generation_type: typeof UPSCALER_GENERATION_TYPE;
  user_prompt: string | null;
  negative_prompt: string | null;
  resemblance: number | null;
  creativity: number | null;
  init_image_uuid: string | null;
  scale_factor: ScaleFactor | null;
  imagination_strength_preset: UpscaleImaginationStrengthPreset | null;
};

export type GenerativeFillGenerationParams = BaseGenerationParams & {
  generation_type: typeof GENERATIVE_FILL_GENERATION_TYPE;
  user_prompt: string | null;
  init_image_uuid: string | null;
  mask_uuid: string | null;
  prompt_strength: number | null;
  references: ModelGenerationParams[] | null;
};

export type BriefGenerationParams = BaseGenerationParams & {
  generation_type: typeof BRIEF_TO_IMAGE_GENERATION_TYPE;
  user_prompt: string | null;
  generated_prompt: string | null;
  generation_ratio: GenerationRatio | null;
  references: ModelGenerationParams[] | null;
};

export type ExpandGenerationParams = BaseGenerationParams & {
  generation_type: typeof EXPAND_GENERATION_TYPE;
  init_image_uuid: string | null;
  user_prompt: string | null;
};

export type EnhanceGenerationParams = BaseGenerationParams & {
  generation_type: typeof ENHANCE_GENERATION_TYPE;
  init_image_uuid: string | null;
};

export type AnimateGenerationParams = BaseGenerationParams & {
  generation_type: typeof ANIMATE_GENERATION_TYPE;
  init_image_uuid: string | null;
  user_prompt: string | null;
};

export type CopyAdGenerationParams = BaseGenerationParams & {
  generation_type: typeof COPY_AD_GENERATION_TYPE;
  init_image_uuid: string | null;
};

type BaseInformation = {
  user_email: string | null;
  generation_parameters:
    | TextToImageGenerationParams
    | EditImageWithPromptGenerationParams
    | ImageToImageGenerationParams
    | UpscalerGenerationParams
    | GenerativeFillGenerationParams
    | ExpandGenerationParams
    | EnhanceGenerationParams
    | StyleTransferGenerationParams
    | BriefGenerationParams
    | AnimateGenerationParams
    | CopyAdGenerationParams
    | null;
};

type BaseContent = {
  resource_type: ResourceType;
  uuid: string;
  url: string | null;
  status: ImageStatus;
  created_at: string;
  width: number | null;
  height: number | null;
  purchased_at: string | null;
  is_curated: boolean;
  is_from_edit: boolean;
  node_uuid: string | null;
  information: BaseInformation;
};

export type Image = BaseContent & {
  resource_type: typeof IMAGE_RESOURCE_TYPE;
};

export type Video = BaseContent & {
  resource_type: typeof VIDEO_RESOURCE_TYPE;
};

export type Content = Image | Video;

export type DownloadContent = {
  uuid: string;
  download_url: string;
  resource_type: ResourceType;
};

export type Asset = {
  uuid: string;
  created_at: string;
  deleted_at: string | null;
  is_in_favorite: boolean;
  tags: Tag[];
  content: Content;
};

// FIXME: The BaseGenerationImage type is used to type data from different routes. It should be split into different types to
//  have task-specific types.

export type NoGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: null;
  };
};

export type TextToImageGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: TextToImageGenerationParams;
  };
};

export type ImageToImageGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: ImageToImageGenerationParams;
  };
};

export type EditImageWithPromptGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: EditImageWithPromptGenerationParams;
  };
};

export type UpscalerGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: UpscalerGenerationParams;
  };
};

export type GenerativeFillGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: GenerativeFillGenerationParams;
  };
};

export type StyleTransferGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: StyleTransferGenerationParams;
  };
};

export type BriefGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: BriefGenerationParams;
  };
};

export type ExpandGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: ExpandGenerationParams;
  };
};

export type EnhanceGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: EnhanceGenerationParams;
  };
};

export type CopyAdGeneratedImage = Image & {
  information: BaseInformation & {
    generation_parameters: CopyAdGenerationParams;
  };
};

export type DetailedImage =
  | NoGeneratedImage
  | TextToImageGeneratedImage
  | EditImageWithPromptGeneratedImage
  | ImageToImageGeneratedImage
  | UpscalerGeneratedImage
  | GenerativeFillGeneratedImage
  | StyleTransferGeneratedImage
  | BriefGeneratedImage
  | ExpandGeneratedImage
  | EnhanceGeneratedImage
  | CopyAdGeneratedImage;

export type NoGeneratedVideo = Video & {
  information: BaseInformation & {
    generation_parameters: null;
  };
};

export type AnimateGeneratedVideo = Video & {
  information: BaseInformation & {
    generation_parameters: AnimateGenerationParams;
  };
};

export type DetailedVideo = NoGeneratedVideo | AnimateGeneratedVideo;

export type DetailedContent = DetailedImage | DetailedVideo;

export type DetailedAsset = Asset & {
  composition_uuid: string | null;
  content: DetailedContent;
};

export type DetailedComposition = {
  uuid: string;
  created_at: string;
  fabric_canvas_data: any;
};

export type ContentDescription = {
  description: string | null;
};

export type NodeGenerationParameters = {
  name: string;
  status: NodeStatus;
  started_at: string | null;
  ended_at: string | null;
  parameters: JSON;
};

export type ImageGenerationInformation = {
  node_generation_parameters: NodeGenerationParameters[];
};

export type NodeStatus = (typeof NODE_STATUS)[number];

export type ScaleFactor = (typeof SCALE_FACTORS)[number];

export type GenerationRatio = (typeof GENERATION_RATIOS)[number];

export type GenerationType = (typeof GENERATION_TYPES)[number];

export type Tool = (typeof TOOLS)[number];
export type CreateTool = (typeof CREATE_TOOLS)[number];
export type EditTool = (typeof EDIT_TOOLS)[number];
export type ImageEditionTool = (typeof IMAGE_EDITION_TOOLS)[number];
export type DesignerTool = (typeof DESIGNER_TOOLS)[number];
export type ModelCompatibleTool = (typeof MODEL_COMPATIBLE_TOOLS)[number];

export const isModelCompatibleTool = (
  tool: string,
): tool is ModelCompatibleTool =>
  (MODEL_COMPATIBLE_TOOLS as readonly string[]).includes(tool);

export type BoardWorkspace = (typeof BOARD_WORKSPACES)[number];

export type AssetEdition = {
  contents: Content[];
  created_at: string;
  uuid: string | null;
  generation_type: GenerationType | null;
};

export type AssetHistory = {
  editions: AssetEdition[];
};

export type RatioDisplay = {
  label: string;
  extraInfo?: string;
  iconName: "Square" | "RectangleVertical" | "RectangleHorizontal";
};

export type EditAssetPanelType = (typeof EDIT_ASSET_PANEL_TYPE)[number];

export type ModelsSettings = {
  generationParams: ModelGenerationParams[];
  models: ModelWithIsCompatibleAndTags[];
  hasExactlyOneCharacterModelSelected: boolean;
  hasExactlyOneObjectModelSelected: boolean;
};

export type ModelActions = {
  addModel: (modelUuid: string, isModelTrained: boolean) => void;
  removeModel: (modelUuid: string) => void;
  updateModelGenerationParams: (
    modelUuid: string,
    extraModelGenerationParams: Partial<ModelExtraGenerationParams>,
  ) => void;
  updateModelGenerationType: (
    modelUuid: string,
    isModelTrained: boolean,
    modelGenerationType: ModelGenerationType,
  ) => void;
  reset: () => void;
};

export type TextToImagePipelineData = {
  type: typeof TEXT_TO_IMAGE_GENERATION_TYPE;
  prompt: string | null;
  generation_ratio: GenerationRatio | null;
  references: ModelGenerationParams[];
};

export type EditImageWithPromptPipelineData = {
  type: typeof EDIT_IMAGE_WITH_PROMPT_GENERATION_TYPE;
  prompt: string | null;
  init_image_uuid: string | null;
};

export type ImageToImagePipelineData = {
  type: typeof IMAGE_TO_IMAGE_GENERATION_TYPE;
  prompt: string | null;
  init_image_uuid: string | null;
  prompt_strength: number | null;
  creativity_strength_preset: VariationCreativityStrengthPreset | null;
  references: ModelGenerationParams[];
};

export type UpscalerPipelineData = {
  type: typeof UPSCALER_GENERATION_TYPE;
  prompt: string | null;
  negative_prompt: string | null;
  resemblance: number | null;
  creativity: number | null;
  init_image_uuid: string | null;
  scale_factor: ScaleFactor | null;
  imagination_strength_preset: UpscaleImaginationStrengthPreset | null;
};

export type GenerativeFillPipelineData = {
  type: typeof GENERATIVE_FILL_GENERATION_TYPE;
  prompt: string | null;
  init_image_uuid: string | null;
  mask_uuid: string | null;
  prompt_strength: number | null;
  references: ModelGenerationParams[];
};

export type BriefPipelineData = {
  type: typeof BRIEF_TO_IMAGE_GENERATION_TYPE;
  prompt: string | null;
  prompts_with_uuids: PromptWithImageUuid[] | null;
  generation_ratios_with_uuids: GenerationRatioWithImageUuid[] | null;
  references: ModelGenerationParams[];
};

export type ExpandPipelineData = {
  type: typeof EXPAND_GENERATION_TYPE;
  init_image_uuid: string | null;
  prompt: string | null;
  generation_ratio: GenerationRatio | null;
};

export type EnhancePipelineData = {
  type: typeof ENHANCE_GENERATION_TYPE;
  init_image_uuid: string | null;
};

export type AnimatePipelineData = {
  type: typeof ANIMATE_GENERATION_TYPE;
  init_image_uuid: string | null;
  user_prompt: string | null;
};

export type BoundingBox = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type TextBox = {
  bbox: BoundingBox;
  text: string;
  font_family: string | null;
  color: HexColor | null;
};

export type Paragraph = {
  alignment: "left" | "center" | "right";
  text_boxes: TextBox[];
};

export type Layout = {
  paragraphs: Paragraph[];
};

export type CopyAdPipelineData = {
  type: typeof COPY_AD_GENERATION_TYPE;
  layout: Layout | null;
};

export type PipelineData = {
  uuid: string;
  pipeline_data: {
    status: NodeStatus;
    started_at: string | null;
    ended_at: string | null;
    data:
      | TextToImagePipelineData
      | EditImageWithPromptPipelineData
      | ImageToImagePipelineData
      | UpscalerPipelineData
      | GenerativeFillPipelineData
      | BriefPipelineData
      | ExpandPipelineData
      | EnhancePipelineData
      | AnimatePipelineData
      | CopyAdPipelineData;
  };
} | null;
